import { useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { hashtagsFor, PLATFORM_LIMITS, MAX_TAG_LENGTH } from "./textUtils";

const TOOL_INFO_CONTENT = {
  what: `Generates relevant hashtags for a piece of content or topic, useful for social media posts where hashtags help with discoverability.`,
  how: `Enter your topic or keywords and press Generate. Suggestions are built from your own words (CamelCase so they read well, and so screen readers can speak them) with c++, c#, f# and .net spelled out. Some suggestions add common endings such as Tips, Life or Lovers. Copy the ones that fit your post.`,
  tip: `A smaller set of specific, relevant hashtags generally performs better than a large block of generic ones — quality and relevance matter more than sheer quantity on most platforms.`,
};

export default function HashtagGenerator() {
  const [topic, setTopic] = useState("");
  const [hashtags, setHashtags] = useState<string[]>([]);

  const [platform, setPlatform] = useState("any");
  const [ascii, setAscii] = useState(false);
  const plat = PLATFORM_LIMITS.find(p => p.id === platform) ?? PLATFORM_LIMITS[0];
  const blank = !topic.trim();

  const { logUsageNow } = useLogUsageOnAction();

  const generate = () => {
    if (blank) { setHashtags([]); return; }
    setHashtags(hashtagsFor(topic, Math.min(15, plat.max ?? 15), { ascii }));
  };
  const copyAll = async () => {
    const allowed = await logUsageNow("copy");
    if (!allowed) return;
    try { await navigator.clipboard.writeText(hashtags.join(" ")); toast.success("Copied!"); } catch { toast.error("Could not copy."); }
  };


  return (
    <ToolContainer>
      <div className="space-y-4">
        <div className="flex gap-3">
          <div className="flex-1"><Label htmlFor="hg-topic">Topic or keyword</Label><Input id="hg-topic" value={topic} maxLength={MAX_TAG_LENGTH} onChange={e => { setTopic(e.target.value); if (!e.target.value.trim()) setHashtags([]); }} placeholder="e.g. travel photography" /></div>
          <Button className="mt-6" onClick={generate} disabled={blank}>Generate</Button>
        </div>
        <p className="text-xs text-muted-foreground -mt-2">Up to {MAX_TAG_LENGTH} characters; each hashtag is cut at {MAX_TAG_LENGTH}.</p>
        <div className="flex flex-wrap items-end gap-4 text-sm">
          <div>
            <Label htmlFor="hg-platform" className="text-xs">Platform</Label>
            <select id="hg-platform" className="border rounded px-2 py-2 bg-background text-sm block" value={platform} onChange={e => { setPlatform(e.target.value); setHashtags([]); }}>
              {PLATFORM_LIMITS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 pb-2"><input type="checkbox" checked={ascii} onChange={e => { setAscii(e.target.checked); setHashtags([]); }} /> Plain letters only (cafe, not café)</label>
        </div>
        <p className="text-xs text-muted-foreground -mt-2">{plat.note}</p>
        {hashtags.length > 0 && (
          <div className="p-4 rounded-lg border bg-card">
            <div className="flex flex-wrap gap-2">
              {hashtags.map((tag, i) => <Badge key={i} variant="secondary" className="text-sm">{tag}</Badge>)}
            </div>
            <Button size="sm" className="mt-3" onClick={copyAll}>Copy All</Button>
          </div>
        )}
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
